import './styles/CompanyItem.scss';

import {
  CheckCircleOutlined,
  Business,
  Person,
  GroupWork,
  Menu,
  Block,
  OpenInBrowser
} from '@mui/icons-material';
import { Grid, Paper, Tooltip, Chip, Fab, MenuItem } from '@mui/material';

import clsx from 'clsx';
import parse from 'html-react-parser';
import { useRef, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { useCompaniesMutation } from 'api/hooks/jobmarket/companies';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import Logo from 'app/components/Logo';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { CompanyDetail } from 'app/containers/Jobmarket/CompanyDetail';
import { ManageOffer } from 'app/containers/Jobmarket/ManageOffer';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { CourseTooltip } from './components/CourseTooltip';
import { LabeledInput } from './components/LabeledInput';

interface Props {
  company: Company;
  studentView?: boolean;
}

export const CompanyItem: FunctionComponent<Props> = ({ company, studentView }) => {
  const { t } = useTranslation();
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [createOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const anchorRef = useRef(null);
  const currentURL = useCurrentURL();
  const { updateState } = useCompaniesMutation();
  const dispatch = useDispatch();

  const handleCompanyUpdate = async () => {
    try {
      await updateState.mutateAsync({
        id: company.id,
        data: {
          state: 'active'
        }
      });
      dispatch(
        AlertActions.success(t(translations.pages.manageCompanies.alerts.changeStateSuccessful()))
      );
      setBanModalOpen(false);
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.manageCompanies.alerts.changeStateFailed()))
      );
    }
  };

  const companyActions = () => {
    if (studentView) {
      return (
        <Link to={`/companies/${company.id}`}>
          <Fab size="small" className="action" ref={anchorRef}>
            <OpenInBrowser />
          </Fab>
        </Link>
      );
    }
    switch (company.state) {
      case 'active':
        return (
          <>
            <Fab size="small" className="action" onClick={() => setMenuOpen(true)} ref={anchorRef}>
              <Menu />
            </Fab>
            <ContextMenu open={menuOpen} setOpen={setMenuOpen} anchorEl={anchorRef.current}>
              <MenuItem
                onClick={() => {
                  setMenuOpen(false);
                  setDetailViewOpen(true);
                }}
              >
                {t(translations.pages.manageCompanies.companyProfile())}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuOpen(false);
                  setCreateOfferModalOpen(true);
                }}
              >
                {t(translations.pages.manageJobOffers.createNew())}
              </MenuItem>
              <Link to={`/companies/${company.id}`} state={{ back: currentURL }}>
                <MenuItem>{t(translations.pages.manageCompanies.jobManagement())}</MenuItem>
              </Link>
            </ContextMenu>
          </>
        );
      case 'inactive':
      case 'archived':
        return (
          <Tooltip
            title={t(translations.pages.manageCompanies.approve()) as string}
            placement="top"
          >
            <Fab
              size="small"
              className="action"
              onClick={() => {
                setDetailViewOpen(true);
              }}
            >
              <CheckCircleOutlined />
            </Fab>
          </Tooltip>
        );
      case 'banned':
        return (
          <>
            <Fab size="small" className="action banned" onClick={() => setBanModalOpen(true)}>
              <Block />
            </Fab>
            <ConfirmationDialog
              open={banModalOpen}
              title={t(translations.pages.manageCompanies.unbanCompany.title(), {
                name: company.name
              })}
              onClose={() => {
                setBanModalOpen(false);
              }}
              onConfirm={() => handleCompanyUpdate()}
            >
              <Grid container>{t(translations.pages.manageCompanies.unbanCompany.message())}</Grid>
              {company.blockReason && <Grid container>{parse(company.blockReason)}</Grid>}
            </ConfirmationDialog>
          </>
        );
      default:
        return null;
    }
  };

  const faculties = company.faculties.map(f => {
    return {
      ...f,
      courses: company.courses.filter(c => c.faculty?.id === f.id)
    };
  });

  return (
    <>
      <FormWrapperModal
        className="company-detail-modal"
        open={detailViewOpen}
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setDetailViewOpen(false);
        }}
      >
        <div className="company-detail-modal__body">
          <CompanyDetail
            companyId={company.id}
            closeHandler={() => {
              setDetailViewOpen(false);
              dispatch(FormErrorActions.close());
            }}
          />
        </div>
      </FormWrapperModal>
      <FormWrapperModal
        className="job-offer-detail-modal"
        open={createOfferModalOpen}
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setCreateOfferModalOpen(false);
        }}
      >
        <div className="job-offer-detail-modal__body">
          <ManageOffer
            company={company}
            newOffer
            closeHandler={() => {
              setCreateOfferModalOpen(false);
            }}
          />
        </div>
      </FormWrapperModal>
      <Grid
        container
        className={clsx('company', {
          'company--active': company.state === 'active',
          'company--inactive': company.state === 'inactive',
          'company--archived': company.state === 'archived',
          'company--banned': company.state === 'banned'
        })}
        component={Paper}
        elevation={2}
      >
        <Grid item xs="auto" className="centered">
          {companyActions()}
        </Grid>
        <Grid item xs={1} className="logo">
          <Logo path="jobmarket/logos" src={company.logo} />
        </Grid>
        <Grid item xs>
          <Grid container>
            <Grid item xs={studentView ? 6 : 4} className="address">
              <Grid container>
                <Business />
                <div className="name">{company.name}</div>
              </Grid>
              <LabeledInput label={t(translations.common.address())}>
                {company.street + ', ' + company.zip + ' ' + company.city}
              </LabeledInput>
              <Grid container>
                <Grid item xs={6}>
                  <LabeledInput label={t(translations.common.phone())}>
                    {company.phone}
                  </LabeledInput>
                </Grid>
                <Grid item xs={6}>
                  <LabeledInput label={t(translations.common.fax())}>{company.fax}</LabeledInput>
                </Grid>
              </Grid>
            </Grid>
            {studentView === false ? (
              <Grid item xs={3} className="contact">
                <Grid container>
                  <Person />
                  <div className="name">
                    {company.mainContact.firstName} {company.mainContact.lastName}
                  </div>
                </Grid>
                <LabeledInput label={t(translations.common.email_short())}>
                  {company.mainContact.email}
                </LabeledInput>
                <Grid container>
                  <Grid item xs={6}>
                    <LabeledInput label={t(translations.common.phone())}>
                      {company.mainContact.phone}
                    </LabeledInput>
                  </Grid>
                  <Grid item xs={6}>
                    <LabeledInput label={t(translations.common.position())}>
                      {company.mainContact.position}
                    </LabeledInput>
                  </Grid>
                </Grid>
              </Grid>
            ) : undefined}
            <Grid item xs={studentView ? 4 : 3} className="faculties">
              <Grid container>
                <GroupWork />
                <div className="name">{t(translations.common.faculties())}</div>
              </Grid>
              {faculties.map(f => (
                <Tooltip
                  className="course-tooltip"
                  key={f.id}
                  placement="left"
                  classes={{ tooltip: 'course-tooltip-wrapper' }}
                  title={
                    <>
                      <CourseTooltip courses={f.courses} title={f.name} />
                    </>
                  }
                >
                  <Chip component={Paper} label={f.shorthand} />
                </Tooltip>
              ))}
            </Grid>
            <Grid item xs={2} className="centered">
              <LabeledInput label={t(translations.pages.manageCompanies.registerDate())}>
                {new Date(company.createdAt).toLocaleDateString()}
              </LabeledInput>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
